import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react";
import DashboardTabItem from "./DashboardTabItem";
import dashboardV2Store from "../store/dashboardV2Store";

const DashboardHeader = observer(() => {
  const [mode, setMode] = useState(null);
  const [dashName, setDashName] = useState("");
  const [editingDashId, setEditingDashId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editAnchorPos, setEditAnchorPos] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [tabMenuOpenId, setTabMenuOpenId] = useState(null);
  const [tabMenuAnchorPos, setTabMenuAnchorPos] = useState(null);
  const dropdownRef = useRef(null);
  const tabMenuRef = useRef(null);
  const tabRefs = useRef({});
  const tabsScrollRef = useRef(null);

  const { dashboards, activeDashboardId, isCreatingDashboard } = dashboardV2Store;

  const closeDropdown = () => {
    setMode(null);
    setDashName("");
    setEditingDashId(null);
    setEditAnchorPos(null);
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        closeDropdown();
      }
    };
    if (mode) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mode]);

  const closeTabMenu = () => {
    setTabMenuOpenId(null);
    setTabMenuAnchorPos(null);
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (tabMenuRef.current && !tabMenuRef.current.contains(e.target)) {
        closeTabMenu();
      }
    };
    if (tabMenuOpenId) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [tabMenuOpenId]);

  const openTabMenu = d => {
    if (tabMenuOpenId === d.id) {
      closeTabMenu();
      return;
    }
    const tabNode = tabRefs.current[d.id];
    const containerNode = dropdownRef.current;
    if (tabNode && containerNode) {
      const tabRect = tabNode.getBoundingClientRect();
      const containerRect = containerNode.getBoundingClientRect();
      setTabMenuAnchorPos({
        right: containerRect.right - tabRect.right,
        top: tabRect.bottom - containerRect.top + 6
      });
    }
    closeDropdown();
    setTabMenuOpenId(d.id);
  };

  const updateScrollAffordance = () => {
    const el = tabsScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    updateScrollAffordance();
    const el = tabsScrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollAffordance);
    window.addEventListener("resize", updateScrollAffordance);
    return () => {
      el.removeEventListener("scroll", updateScrollAffordance);
      window.removeEventListener("resize", updateScrollAffordance);
    };
  }, [dashboards.length]);

  const scrollTabsBy = amount => {
    if (tabsScrollRef.current) {
      tabsScrollRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  const openCreate = () => {
    closeTabMenu();
    setMode(prev => (prev === "create" ? null : "create"));
    setDashName("");
    setEditingDashId(null);
  };

  const openEdit = d => {
    closeTabMenu();
    const tabNode = tabRefs.current[d.id];
    const containerNode = dropdownRef.current;
    if (tabNode && containerNode) {
      const tabRect = tabNode.getBoundingClientRect();
      const containerRect = containerNode.getBoundingClientRect();
      setEditAnchorPos({
        left: tabRect.left - containerRect.left,
        top: tabRect.bottom - containerRect.top + 8
      });
    }
    setMode("edit");
    setDashName(d.name);
    setEditingDashId(d.id);
  };

  const handleSave = async () => {
    const trimmed = dashName.trim();
    if (!trimmed) return;
    setIsSaving(true);
    const success =
      mode === "create"
        ? await dashboardV2Store.createDashboard(trimmed)
        : await dashboardV2Store.renameDashboard(editingDashId, trimmed);
    setIsSaving(false);
    if (success) closeDropdown();
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") closeDropdown();
  };

  const isBusy = mode === "create" ? isCreatingDashboard : isSaving;

  return (
    <div className="dashboardV2TabRow" ref={dropdownRef}>
      <div
        className={`dashboardV2TabsScrollWrap${canScrollLeft ? " hasFadeLeft" : ""}${canScrollRight ? " hasFadeRight" : ""}`}
      >
        <button
          type="button"
          className={`dashboardV2TabScrollBtn dashboardV2TabScrollBtnLeft${canScrollLeft ? "" : " dashboardV2TabScrollBtnHidden"}`}
          onClick={() => scrollTabsBy(-160)}
          title="Scroll left"
          tabIndex={canScrollLeft ? 0 : -1}
        >
          ‹
        </button>
        <div className="dashboardV2Tabs" ref={tabsScrollRef}>
          {dashboards.map(d => (
            <DashboardTabItem
              key={d.id}
              tabRef={node => {
                if (node) tabRefs.current[d.id] = node;
                else delete tabRefs.current[d.id];
              }}
              name={d.name}
              isActive={d.id === activeDashboardId}
              isDefault={d.default}
              isMenuOpen={tabMenuOpenId === d.id}
              onClick={() => dashboardV2Store.setActiveDashboardId(d.id)}
              onMenuClick={() => openTabMenu(d)}
            />
          ))}
        </div>
        <button
          type="button"
          className={`dashboardV2TabScrollBtn dashboardV2TabScrollBtnRight${canScrollRight ? "" : " dashboardV2TabScrollBtnHidden"}`}
          onClick={() => scrollTabsBy(160)}
          title="Scroll right"
          tabIndex={canScrollRight ? 0 : -1}
        >
          ›
        </button>
      </div>

      <div className="dashboardV2AddTabWrap">
        <button
          type="button"
          className="dashboardV2AddTabBtn"
          onClick={e => {
            e.stopPropagation();
            openCreate();
          }}
          title="Create new dashboard"
        >
          +
        </button>

        {mode === "create" && (
          <div
            className="dashboardV2CreateDropdown"
            onClick={e => e.stopPropagation()}
          >
            <p className="dashboardV2CreateDropdownTitle">Name this dashboard</p>
            <input
              autoFocus
              className="dashboardV2CreateDropdownInput"
              value={dashName}
              onChange={e => setDashName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. SDR standup…"
              maxLength={60}
              disabled={isBusy}
            />
            <div className="dashboardV2CreateDropdownActions">
              <button
                type="button"
                className="dashboardV2CreateCancelBtn"
                onClick={closeDropdown}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`dashboardV2CreateSaveBtn${!dashName.trim() || isBusy ? " dashboardV2CreateSaveBtnDisabled" : ""}`}
                onClick={handleSave}
                disabled={!dashName.trim() || isBusy}
              >
                {isBusy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>

      {mode === "edit" && editAnchorPos && (
        <div
          className="dashboardV2CreateDropdown"
          style={{ left: editAnchorPos.left, top: editAnchorPos.top }}
          onClick={e => e.stopPropagation()}
        >
          <p className="dashboardV2CreateDropdownTitle">Edit dashboard</p>
          <input
            autoFocus
            className="dashboardV2CreateDropdownInput"
            value={dashName}
            onChange={e => setDashName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. SDR standup…"
            maxLength={60}
            disabled={isBusy}
          />
          <div className="dashboardV2CreateDropdownActions">
            <button
              type="button"
              className="dashboardV2CreateCancelBtn"
              onClick={closeDropdown}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`dashboardV2CreateSaveBtn${!dashName.trim() || isBusy ? " dashboardV2CreateSaveBtnDisabled" : ""}`}
              onClick={handleSave}
              disabled={!dashName.trim() || isBusy}
            >
              {isBusy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {tabMenuOpenId && tabMenuAnchorPos && (
        <div
          ref={tabMenuRef}
          className="dashboardV2TabDotMenuDropdown"
          style={{ right: tabMenuAnchorPos.right, top: tabMenuAnchorPos.top }}
          onClick={e => e.stopPropagation()}
        >
          <button
            type="button"
            className="dashboardV2TabDotMenuItem"
            onClick={() => {
              const d = dashboards.find(item => item.id === tabMenuOpenId);
              closeTabMenu();
              if (d) openEdit(d);
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="dashboardV2TabDotMenuItem dashboardV2TabDotMenuItemDanger"
            onClick={() => {
              const id = tabMenuOpenId;
              closeTabMenu();
              dashboardV2Store.deleteDashboard(id);
            }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
});

export default DashboardHeader;
